import React, { useCallback, useState, useEffect } from "react";
import { Map, Marker, useMap } from "@vis.gl/react-google-maps";
import Circle from "./Circle";
import Polygon from "./Polygon";
import Polyline from "./Polyline";
import Box from "@mui/material/Box";
import Button from "../Buttons/Button";
import {
  BUTTON_CANCEL,
  BUTTON_CONTINUE,
  BUTTON_DROP_PIN,
  EXPORTING_LOCATIONS_TO_FORM,
  LOADING_LOCATIONS_IN_MAP,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import useMapsFeatures from "../../hooks/useMapsFeatures";
import _ from "lodash";
import SearchLocationMaps from "./SearchLocationMaps";
import MarkersCards from "./MarkersCards";
import SuggestedLocationsBar from "./SuggestedLocationsBar";
import { useDebounced } from "../../hooks/useDebounce";
import {
  FROM_MAP_RADIUS_COMPONENT,
  LARGE_LOCATION_TYPES,
} from "../../utils/constants";
import { CircularProgress, Typography, useMediaQuery } from "@mui/material";
import {
  startGetLocationsPlatformFromMap,
  startGetSuggestedLocations,
} from "../../actions/adsCampaigns";
import { useDispatchApp } from "../../lib/redux";
import { detectOverlappingLocations } from "./utils/verifyOverlap";
import { clampRadiusMeters } from "./utils/radiusLimits";
import { MAPS_RADIUS } from "../../utils/constants";
import ModalOverlapConflicts from "../Modal/ModalOverlapConflicts";

const INITIAL_CENTER = { lat: 41.1897, lng: -96.0627 };

const isBelowCityLocation = (metadata = {}) => {
  const originalTypes = metadata.originalTypes || [];
  const metaTypes = metadata.types || [];
  const typeSet = new Set([...originalTypes, ...metaTypes]);
  const hasLargeType = LARGE_LOCATION_TYPES.some((type) => typeSet.has(type));
  return !hasLargeType;
};

const resolveLocationType = (metadata = {}) => {
  const originalTypes = metadata.originalTypes || [];
  const metaTypes = metadata.types || [];
  const typeSet = new Set([...originalTypes, ...metaTypes]);

  const hasType = (type) => typeSet.has(type);
  if (hasType("country")) {
    return "country";
  }
  if (hasType("administrative_area_level_1")) {
    return "region";
  }
  if (
    hasType("locality") ||
    hasType("administrative_area_level_2") ||
    hasType("administrative_area_level_3") ||
    hasType("sublocality")
  ) {
    return "city";
  }
  return "city";
};

const withLocationType = (marker = {}) => {
  if (marker.locationType) return marker;
  const locationType = resolveLocationType(marker.metadata || {});
  return { ...marker, locationType };
};

const MapRadius = ({
  value = {},
  initialCenter = INITIAL_CENTER,
  sx = {},
  onCallbackSave = () => {},
  // New props
  maxLocations,
  disableRadius,
  disableAISearch,
  platform,
  from,
  integrationID,
  adAccountID,
  campaignContext = "",
}) => {
  const [loadingType, setLoadingType] = useState("loading");
  const [geocoderLoaded, setGeocoderLoaded] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [isDroppingPin, setIsDroppingPin] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [suggestedLocations, setSuggestedLocations] = useState([]);
  const [isLoadingSuggestedLocations, setIsLoadingSuggestedLocations] =
    useState(false);
  const suggestedFetchedRef = React.useRef(false);

  const is320pxMin = useMediaQuery("(max-width: 320px)");

  const modalConflictsOpen = conflicts?.length > 0;

  const { t } = useTranslationApp();
  const map = useMap();
  const dispatch = useDispatchApp();
  const {
    getLocationNameByLatLng,
    geocoder,
    placesService,
    sessionToken,
    getLocationTextByLocationObject,
    getBestLocation,
    getPredictionsByKeyword,
    parseLocationNames,
  } = useMapsFeatures();

  useEffect(() => {
    setContexts(value.contexts || []);
  }, [value.contexts]);

  const onSelectLocationInSearch = useCallback(
    async ({ lat, lng, name, longName, id, metadata }) => {
      const shouldBeRadius = !disableRadius && isBelowCityLocation(metadata);
      const typePointMarker = shouldBeRadius ? "radius" : "specific";
      const radiusPointMarker = shouldBeRadius ? MAPS_RADIUS.MIN_M : "";

      onAddMarker({
        lat,
        lng,
        name,
        longName,
        id,
        type: typePointMarker,
        radius: radiusPointMarker,
        metadata: metadata,
      });
    },
    //eslint-disable-next-line
    [markers.length, maxLocations]
  );

  const onClickMap = useCallback(
    async (e) => {
      if (isDroppingPin) {
        const lat = e.detail.latLng.lat;
        const lng = e.detail.latLng.lng;
        const responseName = await getLocationNameByLatLng({ lat, lng });

        const newMarker = withLocationType({
          lat,
          lng,
          type: disableRadius ? "specific" : "radius",
          radius: disableRadius ? 0 : 10000,
          name: responseName.name,
          longName: responseName.longName,
          id: responseName.id,
          metadata: responseName.metadata || {
            place_id: responseName.id,
            address_components: [],
            types: [],
            formatted_address: "",
            geometry: { location: { lat, lng }, viewport: null },
          },
        });

        if (maxLocations === 1) {
          setMarkers([newMarker]);
          setIsDroppingPin(false);
          return;
        } else if (
          maxLocations !== "infinite" &&
          markers.length >= maxLocations
        ) {
          setIsDroppingPin(false);
          return;
        } else {
          setMarkers((prev) => [...prev, newMarker]);
          setIsDroppingPin(false);
        }
      }
    },
    // eslint-disable-next-line
    [isDroppingPin]
  );

  const updateMarkerInfo = useDebounced(
    useCallback(
      async (index) => {
        setMarkers((currentMarkers) => {
          const marker = currentMarkers[index];
          if (!marker) return currentMarkers;
          (async () => {
            const { lat, lng } = marker;
            const originalTypes = marker.originalTypes || [];

            const responseName = await getLocationNameByLatLng({
              lat,
              lng,
              originalTypes,
            });

            setMarkers((latestMarkers) => {
              const updatedMarkers = [...latestMarkers];
              if (updatedMarkers[index]) {
                const locationType = resolveLocationType(
                  responseName.metadata || {}
                );
                updatedMarkers[index] = {
                  ...updatedMarkers[index],
                  name: responseName.name,
                  longName: responseName.longName,
                  id: responseName.id,
                  metadata: responseName.metadata || {
                    place_id: responseName.id,
                    address_components: [],
                    types: [],
                    formatted_address: "",
                    geometry: { location: { lat, lng }, viewport: null },
                  },
                  locationType,
                };
              }
              return updatedMarkers;
            });
          })();
          return currentMarkers;
        });
      },
      [getLocationNameByLatLng]
    ),
    1000
  );

  const updateMarkerPosition = useCallback(
    ({ index, lat, lng }) => {
      setMarkers((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], lat, lng };
        return updated;
      });

      updateMarkerInfo(index);
    },
    [updateMarkerInfo]
  );

  const updateMarkerRadius = useCallback(({ index, radius }) => {
    setMarkers((prev) => {
      const updated = [...prev];
      const marker = updated[index];
      const clampedRadius =
        marker?.type === "radius" ? clampRadiusMeters(radius) : radius;
      updated[index] = { ...updated[index], radius: clampedRadius };
      return updated;
    });
  }, []);

  const onRemoveMarker = useCallback((index) => {
    setMarkers((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const onMarkerDragEnd = (index, e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    updateMarkerPosition({ index, lat, lng });
  };

  const onSelectLocation = (value) => {
    if (!value || !_.isObject(value) || !placesService) return false;

    // Validate max locations
    if (maxLocations !== "infinite" && markers.length >= maxLocations) {
      return false;
    }

    const isAdded = markers.some((marker) => marker.id === value.place_id);
    const request = {
      placeId: value.place_id,
      fields: [
        "place_id",
        "geometry",
        "address_components",
        "types",
        "formatted_address",
        "name",
      ],
      sessionToken,
    };

    const originalTypes = value.types || [];

    placesService.getDetails(request, async (place, status) => {
      if (status !== "OK" || !place?.geometry?.location) return false;

      const { lat: latFunction, lng: lngFunction } = place.geometry.location;
      const lat = latFunction();
      const lng = lngFunction();
      const responseNames = parseLocationNames(place, originalTypes);
      const { name, longName } = responseNames;

      map.fitBounds(place.geometry?.viewport);
      if (isAdded) return false;
      onSelectLocationInSearch({
        lat,
        lng,
        name: name || value.structured_formatting?.main_text || "",
        longName: longName || value.description || "",
        id: value.place_id,
        metadata: {
          place_id: place.place_id || value.place_id,
          address_components: place.address_components || [],
          types: place.types || value.types || [],
          formatted_address: place.formatted_address || value.description || "",
          geometry: {
            location: { lat, lng },
            viewport: place.geometry?.viewport
              ? {
                  south: place.geometry.viewport.getSouthWest().lat(),
                  west: place.geometry.viewport.getSouthWest().lng(),
                  north: place.geometry.viewport.getNorthEast().lat(),
                  east: place.geometry.viewport.getNorthEast().lng(),
                }
              : null,
          },
          originalTypes,
        },
      });

      return true;
    });
  };

  const onSendLocations = async (forcedMarkers = markers) => {
    if (forcedMarkers.length === 0) {
      onCallbackSave({
        contexts: [],
        locations: [],
      });
      return;
    }

    // Check for overlapping locations before proceeding
    const overlapResult = detectOverlappingLocations(forcedMarkers);
    if (overlapResult.hasOverlap) {
      setConflicts(overlapResult.conflicts);
      return;
    }

    const exportMarkers = forcedMarkers.map((marker) => {
      const locationType = resolveLocationType(marker.metadata || {});
      return {
        ...marker,
        locationType,
      };
    });

    if (from === FROM_MAP_RADIUS_COMPONENT.AD_GROUP) {
      setLoadingType("exporting");
      const response = await dispatch(
        startGetLocationsPlatformFromMap({
          integrationID: integrationID,
          adAccountID: adAccountID,
          locations: exportMarkers,
          platform,
        })
      );
      if (response.ok) {
        const newLocations = response.data;
        onCallbackSave({
          contexts: contexts || [],
          locations: newLocations,
        });
      }
      setLoadingType(null);
      return;
    }
    onCallbackSave({
      contexts: contexts || [],
      locations: exportMarkers,
    });
  };

  const onContinueWithoutOverlaps = () => {
    const indicesToRemove = new Set();
    conflicts.forEach(({ bIndex }) => {
      indicesToRemove.add(bIndex);
    });

    const filteredMarkers = markers.filter(
      (_, index) => !indicesToRemove.has(index)
    );
    setMarkers(filteredMarkers);
    onCloseOverlapModal();
    onSendLocations(filteredMarkers);
  };

  const onCloseOverlapModal = () => {
    setConflicts([]);
  };

  const onAddMarker = ({
    lat,
    lng,
    name,
    longName,
    id,
    type,
    radius,
    metadata,
  }) => {
    const isAdded = markers.some((marker) => marker.id === id);
    if (isAdded) return false;
    const markerBase = withLocationType({
      lat,
      lng,
      name,
      longName,
      id,
      type: type || "specific",
      ...(type === "radius" ? { radius: clampRadiusMeters(radius || 0) } : {}),
      metadata: metadata || {
        place_id: id,
        address_components: [],
        types: [],
        formatted_address: "",
        geometry: { location: { lat, lng }, viewport: null },
      },
    });
    if (maxLocations === 1) {
      setMarkers([markerBase]);
    } else if (maxLocations !== "infinite" && markers.length >= maxLocations) {
      return false;
    } else {
      setMarkers((prev) => [...prev, markerBase]);
    }
  };

  const onSyncLocationsByPlatform = async (locations) => {
    let lastLocation;
    for (const location of locations) {
      const hasRadius = location.radius > 0;
      if (hasRadius) {
        const responseName = await getLocationNameByLatLng({
          lat: location.lat,
          lng: location.lng,
        });
        onAddMarker({
          lat: location.lat,
          lng: location.lng,
          type: "radius",
          radius: clampRadiusMeters(location.radius),
          name: responseName.name,
          longName: responseName.longName,
          id: responseName.id,
          metadata: responseName.metadata || {
            place_id: responseName.id,
            address_components: [],
            types: [],
            formatted_address: "",
            geometry: {
              location: { lat: location.lat, lng: location.lng },
              viewport: null,
            },
          },
        });
        lastLocation = location;
      } else {
        const locationText = getLocationTextByLocationObject(location);
        let results = [];

        try {
          results = await getPredictionsByKeyword(locationText);
        } catch (error) {
          console.log("error", error);
        }
        const bestLocation = getBestLocation({ location, results });
        if (bestLocation) {
          onSelectLocation(bestLocation);
        }
      }
    }
    if (lastLocation) {
      map.setCenter({ lat: lastLocation.lat, lng: lastLocation.lng });
      map.setZoom(12);
    }
    setLoadingType(null);
  };

  useEffect(() => {
    if (!geocoderLoaded) return;

    if (
      from === FROM_MAP_RADIUS_COMPONENT.ONBOARDING ||
      from === FROM_MAP_RADIUS_COMPONENT.AD_ADDRESS
    ) {
      const hydratedLocations = (value.locations || []).map((marker) => {
        const base = withLocationType(marker);
        if (base.type === "radius" && base.radius != null) {
          return { ...base, radius: clampRadiusMeters(base.radius) };
        }
        return base;
      });
      setMarkers(hydratedLocations);
      if (value.locations?.[0]?.lat && value.locations?.[0]?.lng) {
        map.setCenter({
          lat: value.locations?.[0]?.lat,
          lng: value.locations?.[0]?.lng,
        });
        map.setZoom(12);
      }
      setLoadingType(null);
    }
    if (from === FROM_MAP_RADIUS_COMPONENT.AD_GROUP) {
      onSyncLocationsByPlatform(value.locations || []);
    }
    //eslint-disable-next-line
  }, [from, geocoderLoaded]);

  useEffect(() => {
    if (!geocoder) return;
    setGeocoderLoaded(true);
  }, [geocoder]);

  useEffect(() => {
    if (from !== FROM_MAP_RADIUS_COMPONENT.ONBOARDING) {
      return;
    }
    const existingLocations = value.locations || [];
    if (existingLocations.length > 0) {
      return;
    }
    if (suggestedFetchedRef.current) {
      return;
    }
    suggestedFetchedRef.current = true;

    const loadSuggestedLocations = async () => {
      setIsLoadingSuggestedLocations(true);
      const response = await dispatch(
        startGetSuggestedLocations(campaignContext ?? "")
      );
      if (response.ok) {
        setSuggestedLocations(response.data?.locations || []);
      }
      setIsLoadingSuggestedLocations(false);
    };

    loadSuggestedLocations();
    //eslint-disable-next-line
  }, [from, campaignContext, value.locations]);

  const onAddSuggestedLocation = async (location, index) => {
    setSuggestedLocations((prev) => prev.filter((_, i) => i !== index));

    try {
      if (location.lat && location.lng) {
        const responseName = await getLocationNameByLatLng({
          lat: location.lat,
          lng: location.lng,
        });
        onAddMarker({
          lat: location.lat,
          lng: location.lng,
          type: "specific",
          name: responseName.name || location.city || "",
          longName:
            responseName.longName ||
            [location.city, location.region, location.country]
              .filter(Boolean)
              .join(", "),
          id: responseName.id,
          metadata: responseName.metadata || {
            place_id: responseName.id,
            address_components: [],
            types: [],
            formatted_address: "",
            geometry: {
              location: { lat: location.lat, lng: location.lng },
              viewport: null,
            },
          },
        });
        if (map) {
          map.setCenter({ lat: location.lat, lng: location.lng });
          map.setZoom(10);
        }
        return;
      }

      const locationText = [location.city, location.region, location.country]
        .filter(Boolean)
        .join(", ");
      if (!locationText || !geocoder) return;

      const response = await geocoder.geocode({ address: locationText });
      const results = response.results || [];
      if (!results[0]) return;

      const result = results[0];
      const lat = result.geometry.location.lat();
      const lng = result.geometry.location.lng();

      onAddMarker({
        lat,
        lng,
        type: "specific",
        name:
          location.city ||
          result.address_components?.[0]?.long_name ||
          result.address_components?.[0]?.short_name ||
          "",
        longName:
          [location.city, location.region, location.country]
            .filter(Boolean)
            .join(", ") || result.formatted_address,
        id: result.place_id,
        metadata: {
          place_id: result.place_id,
          address_components: result.address_components || [],
          types: result.types || [],
          formatted_address: result.formatted_address || "",
          geometry: {
            location: { lat, lng },
            viewport: result.geometry?.viewport
              ? {
                  south: result.geometry.viewport.getSouthWest().lat(),
                  west: result.geometry.viewport.getSouthWest().lng(),
                  north: result.geometry.viewport.getNorthEast().lat(),
                  east: result.geometry.viewport.getNorthEast().lng(),
                }
              : null,
          },
        },
      });

      if (map && result.geometry?.viewport) {
        map.fitBounds(result.geometry.viewport);
      }
    } catch (error) {
      console.error("Error geocoding suggested location:", location, error);
    }
  };

  const onAddAllSuggestedLocations = async () => {
    const locationsToAdd = [...suggestedLocations];
    setSuggestedLocations([]);
    for (let i = 0; i < locationsToAdd.length; i++) {
      await onAddSuggestedLocation(locationsToAdd[i], 0);
    }
  };

  const showSuggestionsPanel =
    from === FROM_MAP_RADIUS_COMPONENT.ONBOARDING &&
    (value.locations || []).length === 0 &&
    (isLoadingSuggestedLocations || suggestedLocations.length > 0);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        ...sx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          cursor: isDroppingPin ? "crosshair" : "grab",
          "& *": {
            cursor: isDroppingPin ? "crosshair" : "grab",
          },
        }}
      >
        {loadingType && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="white" />
            <Typography
              variant="h6"
              sx={{
                color: "white !important",
              }}
            >
              {t(
                loadingType === "loading"
                  ? LOADING_LOCATIONS_IN_MAP
                  : EXPORTING_LOCATIONS_TO_FORM
              )}
            </Typography>
          </Box>
        )}

        <Map
          defaultCenter={initialCenter}
          defaultZoom={5}
          gestureHandling={"greedy"}
          disableDefaultUI={true}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          onClick={onClickMap}
        >
          {markers.map((marker, index) => {
            const { lat, lng, type, radius } = marker;
            if (type === "specific") {
              return (
                <Marker
                  key={index}
                  position={{ lat, lng }}
                  draggable
                  onDragEnd={(e) => onMarkerDragEnd(index, e)}
                />
              );
            }
            if (type === "radius") {
              return (
                <React.Fragment key={index}>
                  <Marker
                    position={{ lat, lng }}
                    draggable
                    onDragEnd={(e) => onMarkerDragEnd(index, e)}
                  />
                  <Circle
                    center={{ lat, lng }}
                    radius={clampRadiusMeters(radius)}
                    minRadius={MAPS_RADIUS.MIN_M}
                    maxRadius={MAPS_RADIUS.MAX_M}
                    draggable
                    editable
                    strokeColor="#0c4cb3"
                    fillColor="#3b82f6"
                    fillOpacity={0.3}
                    strokeWeight={3}
                    onCenterChanged={(googleCenter) => {
                      const newLat = googleCenter.lat();
                      const newLng = googleCenter.lng();
                      updateMarkerPosition({
                        index,
                        lat: newLat,
                        lng: newLng,
                      });
                    }}
                    onRadiusChanged={(newRadius) => {
                      updateMarkerRadius({ index, radius: newRadius });
                    }}
                  />
                </React.Fragment>
              );
            }

            return null;
          })}
          <Polygon strokeWeight={1.5} />
          <Polyline strokeWeight={10} strokeColor={"#ff22cc88"} />
        </Map>

        <Box
          sx={{
            position: "absolute",
            bottom: is320pxMin ? 72 : 16,
            left: is320pxMin ? "unset" : 8,
            right: is320pxMin ? 8 : 0,
            zIndex: 1,
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setIsDroppingPin((prev) => !prev)}
            sx={{
              width: 120,
            }}
          >
            <LocationOnIcon sx={{ mr: 0.5 }} />
            {isDroppingPin ? t(BUTTON_CANCEL) : t(BUTTON_DROP_PIN)}
          </Button>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 8,
            zIndex: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={() => onSendLocations()}
            color="primary"
          >
            {t(BUTTON_CONTINUE)}
          </Button>
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            zIndex: 1,
            width: "100%",
            p: 2,
          }}
        >
          <SearchLocationMaps
            onAddContext={(context) =>
              setContexts((prev) => [...prev, context])
            }
            onSelectLocation={onSelectLocation}
            disableAISearch={disableAISearch}
            onAddMarker={onAddMarker}
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 80,
            zIndex: 1,
            width: "100%",
            p: 2,
          }}
        >
          <MarkersCards
            markers={markers}
            onRemoveMarker={onRemoveMarker}
            onUpdateMarkerRadius={({ index, radius }) => {
              updateMarkerRadius({ index, radius });
            }}
          />
        </Box>
        {modalConflictsOpen && (
          <ModalOverlapConflicts
            open={modalConflictsOpen}
            onClose={onCloseOverlapModal}
            conflicts={conflicts}
            onContinueWithoutOverlaps={onContinueWithoutOverlaps}
          />
        )}

        {showSuggestionsPanel && (
          <SuggestedLocationsBar
            suggestedLocations={suggestedLocations}
            isLoadingSuggestedLocations={isLoadingSuggestedLocations}
            onAddAllSuggestedLocations={onAddAllSuggestedLocations}
            onAddSuggestedLocation={onAddSuggestedLocation}
          />
        )}
      </Box>
    </Box>
  );
};

export default MapRadius;
